import {
  LanguageJsonldLanguageRead,
  PrintingOrderJsonldPrintingOrderRead,
  PrintingOrderJsonldPrintingOrderWrite,
} from '@/api/generated';
import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useModal } from 'context/Modal/Context';
import { MODAL_NAME } from 'context/Modal/MODAL_NAME';
import { useAllLanguagesOptions } from 'hooks/options/useLanguagesOptions';
import { useUserAddresses } from 'hooks/queries/address';
import useApi from 'hooks/useApi';
import useUserApi from 'hooks/UserApi/useUserApi';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import printingFactory from 'utils/factories/printing';
import { mapApiErrorsToFormFields } from 'utils/form/validation';
import * as yup from 'yup';

type HookProps = {
  orderName?: string;
  orderLang?: LanguageJsonldLanguageRead;
  id?: string;
  onSubmit?: (data: PrintingOrderJsonldPrintingOrderRead) => void;
  closeModal: () => void;
};

const printingSchema = () =>
  yup
    .object({
      name: yup.string().required(),
      language: yup.string().required(),
      id: yup.string(),
      shippingValidatedAddress: yup.string(),
      billingValidatedAddress: yup.string(),
    })
    .required();

type FormValues = yup.InferType<ReturnType<typeof printingSchema>>;

const useCreatePrintingModal = ({ orderName, orderLang, id: currentId, onSubmit, closeModal }: HookProps) => {
  const apiClient = useApi();
  const { replaceModal } = useModal();
  const queryClient = useQueryClient();
  const formMethods = useForm<FormValues>({
    mode: 'onSubmit',
    resolver: yupResolver(printingSchema()),
    defaultValues: {
      name: orderName,
      language: orderLang?.['@id'],
      id: currentId,
    },
  });
  const { data: printingOrder } = useQuery({
    queryKey: printingFactory.details(currentId),
    queryFn: () => apiClient.printingOrders.apiPrintingOrdersIdGet(currentId!),
    enabled: !!currentId,
  });
  const { languagesOptions } = useAllLanguagesOptions();
  const { defaultShippingAddress, defaultBillingAddress } = useUserAddresses();
  const { user } = useUserApi();

  const create = useMutation({
    mutationFn: async ({ data }: { data: PrintingOrderJsonldPrintingOrderWrite }) =>
      apiClient.printingOrders.apiPrintingOrdersPost(data),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: printingFactory.all() });
      if (onSubmit) {
        onSubmit(res.data);
      }
      closeModal();
    },
    onError: (res: any) => {
      if (res.error['hydra:description'] === 'printing-order-already-exists') {
        replaceModal(MODAL_NAME.CMS_MODAL, {
          cmsModalName: 'printing-existing-draft-order-warning',
        });
      }
    },
  });

  const update = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: PrintingOrderJsonldPrintingOrderWrite }) =>
      apiClient.printingOrders.apiPrintingOrdersIdPut(id, data),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: printingFactory.all() });
      if (onSubmit) {
        onSubmit(res.data);
      }
      closeModal();
    },
  });

  const handleSubmit = async ({ id, ...data }: FormValues) => {
    if (id) {
      return update.mutateAsync({ id, data }).catch(mapApiErrorsToFormFields(formMethods.setError));
    }

    return create.mutateAsync({ data }).catch(mapApiErrorsToFormFields(formMethods.setError));
  };

  useEffect(() => {
    if (!currentId) {
      if (defaultShippingAddress && defaultBillingAddress?.isEligible) {
        formMethods.setValue('shippingValidatedAddress', defaultShippingAddress['@id']);
      }
      if (defaultBillingAddress && defaultBillingAddress.isEligible) {
        formMethods.setValue('billingValidatedAddress', defaultBillingAddress['@id']);
      }
    } else {
      formMethods.setValue('shippingValidatedAddress', printingOrder?.data?.shippingValidatedAddress?.['@id']);
      formMethods.setValue('billingValidatedAddress', printingOrder?.data?.billingValidatedAddress?.['@id']);
    }
  }, [defaultShippingAddress, defaultBillingAddress, printingOrder]);

  useEffect(() => {
    if (!orderLang && user?.language) {
      formMethods.setValue('language', user.language['@id']!);
    }
  }, [user, orderLang]);

  return {
    formMethods,
    handleSubmit: formMethods.handleSubmit(handleSubmit),
    languagesOptions,
  };
};

export default useCreatePrintingModal;
